import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { AdHeroSlide, trackAdEvent } from "@/components/AdPlacement";

const GOLD = "#D4A017";

/* P0.3 delivery modules. Each is a SEPARATE sponsored surface that fails CLOSED (renders
   null on empty/error) and never touches the organic Marketplace query, pagination or order.
   Sponsored disclosure is always shown. */

function useDelivery(url, params) {
  const [data, setData] = useState(null);
  const key = JSON.stringify(params || {});
  useEffect(() => {
    let alive = true;
    api.get(url, { params: params || {} })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [url, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return data;
}

/* ---- Marketplace Hero Spotlight (rotating; desktop hero band, mobile below filters) ---- */
export function MarketplaceHero({ params }) {
  const data = useDelivery("/marketplace/hero", params);
  const campaigns = data?.campaigns || [];
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (campaigns.length <= 1) return;
    const t = setInterval(() => setActive((i) => (i + 1) % campaigns.length), 9000);
    return () => clearInterval(t);
  }, [campaigns.length]);
  if (!campaigns.length) return null; // fail closed
  const c = campaigns[active % campaigns.length];
  return (
    <div data-testid="marketplace-hero-spotlight" className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-2">
      <div className="w-full sm:w-[40%] sm:min-w-[380px] h-56 sm:h-64">
        <AdHeroSlide key={c.id} campaign={c} placement="MARKETPLACE_HERO_SPOTLIGHT" />
      </div>
      {campaigns.length > 1 && (
        <div className="flex items-center gap-2 mt-2" data-testid="marketplace-hero-dots">
          {campaigns.map((_, i) => (
            <button key={i} data-testid={`marketplace-hero-dot-${i}`} aria-label={`Show sponsored ${i + 1}`}
              aria-current={i === active} onClick={() => setActive(i)} className="h-1.5 rounded-full transition-all"
              style={{ width: i === active ? 22 : 8, backgroundColor: i === active ? GOLD : "rgba(15,44,89,0.2)" }} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- Featured Property (separate sponsored row; reuses the organic ListingCard renderer) ---- */
export function FeaturedRow({ params, renderCard }) {
  const data = useDelivery("/marketplace/featured", params);
  const items = data?.items || [];
  const fired = useRef(new Set());
  useEffect(() => {
    items.forEach((p) => {
      const k = `f:${p.campaign_id}`;
      if (fired.current.has(k)) return;
      fired.current.add(k);
      trackAdEvent("impression", { id: p.campaign_id, cta_destination: `/property/${p.id}`, disclosure: p.disclosure || "Featured" }, "MARKETPLACE_TOP_PUBLIC");
    });
  }, [items]);
  if (!items.length) return null; // fail closed
  return (
    <div data-testid="featured-property-module" className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-2">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#0F2C59]">Featured Properties</span>
        <span data-testid="featured-disclosure" className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-white" style={{ background: GOLD }}>Sponsored</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {items.map((p) => (
          <div key={`feat-${p.campaign_id}`} data-testid={`featured-card-${p.id}`}
            onClick={() => trackAdEvent("click", { id: p.campaign_id, cta_destination: `/property/${p.id}`, disclosure: p.disclosure || "Featured" }, "MARKETPLACE_TOP_PUBLIC")}>
            {renderCard(p)}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---- Promoted Events (canonical Event links; disclosed) ---- */
export function PromotedEventsRow() {
  const data = useDelivery("/events/promoted", {});
  const items = data?.items || [];
  const fired = useRef(new Set());
  useEffect(() => {
    items.forEach((e) => {
      const k = `pe:${e.campaign_id}`;
      if (fired.current.has(k)) return;
      fired.current.add(k);
      trackAdEvent("impression", { id: e.campaign_id, cta_destination: e.cta_destination, disclosure: e.disclosure || "Promoted" }, "MARKETPLACE_TOP_PUBLIC");
    });
  }, [items]);
  if (!items.length) return null; // fail closed
  return (
    <div data-testid="promoted-events-module" className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-2">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#0F2C59]">Promoted Events</span>
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-white" style={{ background: GOLD }}>Sponsored</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((e) => (
          <Link key={`pe-${e.campaign_id}`} to={e.cta_destination || `/events/${e.event_id}`} data-testid={`promoted-event-${e.event_id}`}
            onClick={() => trackAdEvent("click", { id: e.campaign_id, cta_destination: e.cta_destination, disclosure: e.disclosure || "Promoted" }, "MARKETPLACE_TOP_PUBLIC")}
            className="rounded-2xl border border-[#0F2C59]/10 bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="h-32 bg-[#0F2C59]/5 relative">
              {e.cover_image ? <img src={e.cover_image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><CalendarDays className="h-8 w-8 text-[#0F2C59]/30" /></div>}
              <span className="absolute top-2 left-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-white" style={{ background: GOLD }}>Promoted</span>
            </div>
            <div className="p-3">
              <div className="text-[14px] font-semibold text-slate-900 truncate">{e.title}</div>
              {e.city ? <div className="text-[12px] text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> {e.city}</div> : null}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
